import {
  type DeletedRecord,
  type JournalAction,
  type JournalAttempt,
  type JournalEntry,
  REVERT_KIND_LABELS,
  type RevertTarget,
  type RevertView,
} from '@epc/contracts';
import { ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { dateTime, plural } from '../../../lib/format';
import { Button } from '../../../ui/button';
import { Badge, EmptyState, ErrorBox, Spinner } from '../../../ui/feedback';
import {
  ActionStateBadge,
  actorName,
  attemptReason,
  ChangeRows,
  CountBadges,
  type JournalQueryParams,
  methodWord,
  OP_WORD,
  routeText,
  sectionTitle,
  useJournalList,
} from './shared';

type Handlers = {
  canRevert: boolean;
  onAction: (key: string) => void;
  onRecord: (record: string) => void;
  onRevert: (target: RevertTarget, title: string, description?: string) => void;
};

/** Что нужно списку от запроса с подгрузкой */
type PagedQuery<T> = {
  isPending: boolean;
  isError: boolean;
  error: unknown;
  data: { pages: T[][] } | undefined;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => unknown;
};

/** Список с подгрузкой: загрузка, ошибка, пусто, «Показать ещё» */
function Paged<T>({
  q,
  empty,
  children,
}: {
  q: PagedQuery<T>;
  empty: ReactNode;
  children: (items: T[]) => ReactNode;
}) {
  if (q.isPending)
    return (
      <div className="px-5">
        <Spinner />
      </div>
    );
  if (q.isError)
    return (
      <div className="p-5">
        <ErrorBox error={q.error} />
      </div>
    );
  const items = q.data?.pages.flat() ?? [];
  if (!items.length) return <div className="p-5">{empty}</div>;
  return (
    <>
      {children(items)}
      {q.hasNextPage ? (
        <div className="border-t border-line px-5 py-3">
          <Button size="sm" onClick={() => q.fetchNextPage()} loading={q.isFetchingNextPage}>
            Показать ещё
          </Button>
        </div>
      ) : null}
    </>
  );
}

function Meta({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap items-baseline gap-x-1.5 text-[13px] text-ink-2">{children}</div>;
}

function LinkButton({
  onClick,
  children,
  danger,
}: {
  onClick: () => void;
  children: ReactNode;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex min-h-11 items-center text-[13px] font-medium underline underline-offset-2 md:min-h-0 ${
        danger ? 'text-danger' : 'text-ink'
      }`}
    >
      {children}
    </button>
  );
}

/* ───────────────────────── Действия ───────────────────────── */

export function ActionsList({ query, ...h }: { query: JournalQueryParams } & Handlers) {
  const q = useJournalList<JournalAction>('/admin/journal/actions', query);
  return (
    <Paged
      q={q}
      empty={
        <EmptyState title="Действий не найдено">
          Здесь — каждое сохранение, удаление и проведение: кто, когда, откуда и что изменилось.
        </EmptyState>
      }
    >
      {(items) => (
        <ul className="divide-y divide-line">
          {items.map((a) => {
            const body = (
              <>
                <Meta>
                  <span className="num">{dateTime(a.at)}</span>
                  <span aria-hidden>·</span>
                  <span className="font-medium text-ink">{actorName(a.actor)}</span>
                  {a.device || a.ip ? (
                    <>
                      <span aria-hidden>·</span>
                      <span>{[a.device, a.ip].filter(Boolean).join(' · ')}</span>
                    </>
                  ) : null}
                </Meta>
                <div className="text-[14px] font-medium [overflow-wrap:anywhere] text-ink">{a.title}</div>
                {a.records.length > 1 ? (
                  <div className="text-[13px] [overflow-wrap:anywhere] text-ink-2">
                    {a.records
                      .slice(1)
                      .map(
                        (r) =>
                          `${OP_WORD[r.op].toLowerCase()}: ${r.tableTitle}${r.label ? ` ${r.label}` : ''}`,
                      )
                      .join('; ')}
                    {a.total > a.records.length
                      ? `; и ещё ${plural(a.total - a.records.length, 'запись', 'записи', 'записей')}`
                      : ''}
                  </div>
                ) : null}
                <div className="flex flex-wrap gap-1.5">
                  <CountBadges counts={a.counts} />
                  <ActionStateBadge a={a} />
                  {a.sections.slice(0, 3).map((s) => (
                    <Badge key={s} tone="neutral">
                      {sectionTitle(s)}
                    </Badge>
                  ))}
                </div>
              </>
            );
            return (
              <li key={a.key}>
                {a.actor ? (
                  <button
                    type="button"
                    onClick={() => h.onAction(a.key)}
                    className="flex w-full items-center gap-3 px-5 py-3 text-left hover:bg-surface-2"
                  >
                    <div className="flex min-w-0 flex-1 flex-col gap-1">{body}</div>
                    <ChevronRight className="size-4 shrink-0 text-ink-3" aria-hidden />
                  </button>
                ) : (
                  // Изменения, которые портал сделал сам (обмен, фоновые задачи): без отмены из журнала
                  <div className="flex flex-col gap-1 px-5 py-3">{body}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Paged>
  );
}

/* ───────────────────────── Изменения по одному ───────────────────────── */

export function ChangesList({ query, ...h }: { query: JournalQueryParams } & Handlers) {
  const q = useJournalList<JournalEntry>('/admin/journal/changes', query);
  return (
    <Paged q={q} empty={<EmptyState title="Изменений не найдено">Измените отбор или период.</EmptyState>}>
      {(items) => (
        <ul className="divide-y divide-line">
          {items.map((e) => (
            <li key={e.id} className="flex flex-col gap-0.5 px-5 py-3">
              <Meta>
                <span className="num">{dateTime(e.at)}</span>
                <span aria-hidden>·</span>
                <span className="font-medium text-ink">{actorName(e.actor)}</span>
                <span aria-hidden>·</span>
                <span>{sectionTitle(e.section)}</span>
              </Meta>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[14px] text-ink">
                <span className="[overflow-wrap:anywhere]">
                  <b className="font-semibold">{OP_WORD[e.op]}</b> · {e.tableTitle}
                  {e.recordLabel ? ` ${e.recordLabel}` : ''}
                </span>
                {e.reverted ? <Badge tone="warning">отменено</Badge> : null}
              </div>
              <ChangeRows op={e.op} table={e.table} changes={e.changes} limit={5} />
              <div className="flex flex-wrap gap-x-4">
                {e.rowId ? (
                  <LinkButton onClick={() => h.onRecord(`${e.schema}.${e.table}:${e.rowId}`)}>
                    История записи
                  </LinkButton>
                ) : null}
                {e.actionKey ? (
                  <LinkButton onClick={() => h.onAction(e.actionKey!)}>Всё действие</LinkButton>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Paged>
  );
}

/* ───────────────────────── Удалённые записи ───────────────────────── */

export function DeletedList({ query, ...h }: { query: JournalQueryParams } & Handlers) {
  const q = useJournalList<DeletedRecord>('/admin/journal/deleted', query);
  return (
    <Paged
      q={q}
      empty={
        <EmptyState title="Удалённых записей нет">
          Здесь появятся удалённые договоры, заявки, документы и другие записи — их можно восстановить вместе
          со всем, что удалилось вместе с ними.
        </EmptyState>
      }
    >
      {(items) => (
        <ul className="divide-y divide-line">
          {items.map((d) => {
            const label = [d.tableTitle, d.recordLabel].filter(Boolean).join(' ');
            return (
              <li key={d.historyId} className="flex flex-col gap-1 px-5 py-3">
                <Meta>
                  <span>удалено</span>
                  <span className="num">{dateTime(d.at)}</span>
                  <span aria-hidden>·</span>
                  <span className="font-medium text-ink">{actorName(d.actor)}</span>
                  <span aria-hidden>·</span>
                  <span>{sectionTitle(d.section)}</span>
                </Meta>
                <div className="text-[14px] font-medium [overflow-wrap:anywhere] text-ink">{label}</div>
                {d.alsoDeleted ? (
                  <div className="text-[13px] text-ink-2">
                    Вместе с ней удалено связанных записей: {d.alsoDeleted} — они восстановятся тоже
                  </div>
                ) : null}
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                  {h.canRevert ? (
                    <Button
                      size="sm"
                      onClick={() =>
                        h.onRevert(
                          { kind: 'restore', historyId: d.historyId },
                          'Восстановить удалённое',
                          label,
                        )
                      }
                    >
                      Восстановить
                    </Button>
                  ) : null}
                  {d.rowId ? (
                    <LinkButton onClick={() => h.onRecord(`${d.schema}.${d.table}:${d.rowId}`)}>
                      История записи
                    </LinkButton>
                  ) : null}
                  {d.actionKey ? (
                    <LinkButton onClick={() => h.onAction(d.actionKey!)}>Всё действие</LinkButton>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Paged>
  );
}

/* ───────────────────────── Отмены ───────────────────────── */

export function RevertsList(h: Handlers) {
  const q = useJournalList<RevertView>('/admin/journal/reverts', {});
  return (
    <Paged
      q={q}
      empty={
        <EmptyState title="Отмен ещё не было">
          Каждая отмена записывается сюда с причиной. Отмену тоже можно отменить — изменения вернутся.
        </EmptyState>
      }
    >
      {(items) => (
        <ul className="divide-y divide-line">
          {items.map((r) => (
            <li key={r.id} className="flex flex-col gap-1 px-5 py-3">
              <Meta>
                <span className="num">{dateTime(r.at)}</span>
                <span aria-hidden>·</span>
                <span className="font-medium text-ink">{actorName(r.actor)}</span>
                <span aria-hidden>·</span>
                <span>{REVERT_KIND_LABELS[r.kind]}</span>
              </Meta>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="text-[14px] font-medium [overflow-wrap:anywhere] text-ink">{r.title}</span>
                {r.undone ? <Badge>отменена</Badge> : <Badge tone="info">действует</Badge>}
              </div>
              <p className="text-[13px] [overflow-wrap:anywhere] text-ink">Причина: {r.reason}</p>
              <div className="text-[13px] text-ink-2">
                {[
                  r.counts.restored ? `восстановлено ${r.counts.restored}` : null,
                  r.counts.updated ? `изменено ${r.counts.updated}` : null,
                  r.counts.deleted ? `удалено ${r.counts.deleted}` : null,
                ]
                  .filter(Boolean)
                  .join(', ') || 'без изменений'}
                {r.requests > 1 ? ` · действий: ${r.requests}` : ''}
              </div>
              {r.undone ? (
                <p className="text-[13px] [overflow-wrap:anywhere] text-ink-2">
                  Отменена {dateTime(r.undone.at)}, {actorName(r.undone.actor)}: {r.undone.reason}
                </p>
              ) : null}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                {h.canRevert && !r.undone ? (
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => h.onRevert({ kind: 'undo', revertId: r.id }, 'Отменить отмену', r.title)}
                  >
                    Отменить отмену
                  </Button>
                ) : null}
                <LinkButton onClick={() => h.onAction(r.actionKey)}>Что изменила отмена</LinkButton>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Paged>
  );
}

/* ───────────────────────── Отказы и ошибки ───────────────────────── */

export function AttemptsList({ query }: { query: JournalQueryParams }) {
  const q = useJournalList<JournalAttempt>('/admin/journal/attempts', {
    from: query.from,
    to: query.to,
    actorId: query.actorId,
  });
  return (
    <Paged
      q={q}
      empty={
        <EmptyState title="Отказов и ошибок нет">
          Здесь — попытки, которые ничего не изменили: не хватило права, данные не прошли проверку или
          случился сбой.
        </EmptyState>
      }
    >
      {(items) => (
        <ul className="divide-y divide-line">
          {items.map((a) => (
            <li key={`${a.id}-${a.at}`} className="flex flex-col gap-0.5 px-5 py-3">
              <Meta>
                <span className="num">{dateTime(a.at)}</span>
                <span aria-hidden>·</span>
                <span className="font-medium text-ink">{actorName(a.actor)}</span>
                {a.device || a.ip ? (
                  <>
                    <span aria-hidden>·</span>
                    <span>{[a.device, a.ip].filter(Boolean).join(' · ')}</span>
                  </>
                ) : null}
              </Meta>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[14px] text-ink">
                <span>{attemptReason(a.httpStatus)}</span>
                {a.httpStatus ? (
                  <Badge tone={a.httpStatus >= 500 ? 'danger' : a.httpStatus === 403 ? 'warning' : 'neutral'}>
                    {a.httpStatus}
                  </Badge>
                ) : null}
              </div>
              <div className="text-[13px] [overflow-wrap:anywhere] text-ink-2">
                {methodWord(a.method)} · {routeText(a.route) ?? '—'}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Paged>
  );
}
